/**
 * Phira's chart viewport and letterboxing, `Resource::update_size` (W1-05a engine-eval; rendering.md R§1.1-1.2,
 * R§10, rpe-format.md F§4, F§14).
 *
 *   chartAr = player aspect override ?? info.yml aspectRatio (default 16/9)
 *   ar      = forceAspectRatio ? chartAr : min(chartAr, winW / winH)     (a narrower window uses its own ar)
 *   fit an ar-shaped rectangle into the window, centred:
 *     ew = winH·ar;  (W, H) = ew > winW ? (winW, winW / ar) : (ew, winH)
 *     vx = round((winW - W) / 2), vy = round((winH - H) / 2); W and H truncated to integers
 * World (chart space) is x in [-1, 1] across W and y in [-1/ar, 1/ar] up; one world unit is W/2 px on both axes.
 *
 * Internal API: computeViewport, chartAspect, worldToPxMatrix, uiToPxMatrix, worldToPx, pxToWorld.
 */

import type { Mat2D, Vec2 } from '../util/types';
import type { Viewport } from './types';

/** Phira's default aspect ratio (info.yml aspectRatio). */
export const DEFAULT_ASPECT = 16 / 9;

export interface AspectSettings {
  /** info.yml aspectRatio. */
  readonly aspectRatio: number;
  /** info.yml forceAspectRatio. */
  readonly forceAspectRatio: boolean;
}

/** The chart's own aspect: the player override when set, else info.yml aspectRatio (invalid values -> 16/9). */
export function chartAspect(settings: AspectSettings, aspectOverride: number | null = null): number {
  const v = aspectOverride ?? settings.aspectRatio;
  return Number.isFinite(v) && v > 0 ? v : DEFAULT_ASPECT;
}

/** Rust `f32::round` for the non-negative pixel offsets (halves away from zero). */
function roundHalfAway(v: number): number {
  return v < 0 ? -Math.round(-v) : Math.round(v);
}

/** Letterboxed viewport of a winW x winH device-pixel canvas. */
export function computeViewport(
  winW: number,
  winH: number,
  settings: AspectSettings,
  aspectOverride: number | null = null,
): Viewport {
  const chartAr = chartAspect(settings, aspectOverride);
  const winAr = winH > 0 ? winW / winH : chartAr;
  const ar = settings.forceAspectRatio ? chartAr : Math.min(chartAr, winAr);
  let w = winH * ar;
  let h = winH;
  if (w > winW) {
    w = winW;
    h = winW / ar;
  }
  const vx = roundHalfAway((winW - w) / 2);
  const vy = roundHalfAway((winH - h) / 2);
  return { winW, winH, vx, vy, W: Math.trunc(w), H: Math.trunc(h), ar };
}

/** Chart space (y up) -> canvas pixels: [W/2, 0, 0, -W/2, vx + W/2, vy + H/2]. */
export function worldToPxMatrix(vp: Viewport): Mat2D {
  return [vp.W / 2, 0, 0, -vp.W / 2, vp.vx + vp.W / 2, vp.vy + vp.H / 2];
}

/** UI / camera space (y down) -> canvas pixels: [W/2, 0, 0, W/2, vx + W/2, vy + H/2]. */
export function uiToPxMatrix(vp: Viewport): Mat2D {
  return [vp.W / 2, 0, 0, vp.W / 2, vp.vx + vp.W / 2, vp.vy + vp.H / 2];
}

/** A chart-space point in canvas pixels. */
export function worldToPx(vp: Viewport, x: number, y: number): Vec2 {
  return [vp.vx + ((x + 1) * vp.W) / 2, vp.vy + vp.H / 2 - (y * vp.W) / 2];
}

/** A canvas pixel in chart space (inverse of worldToPx). */
export function pxToWorld(vp: Viewport, px: number, py: number): Vec2 {
  return [((px - vp.vx) * 2) / vp.W - 1, ((vp.vy + vp.H / 2 - py) * 2) / vp.W];
}

/** True when a canvas pixel lies inside the chart viewport (outside = the letterbox bars). */
export function insideViewport(vp: Viewport, px: number, py: number): boolean {
  return px >= vp.vx && px < vp.vx + vp.W && py >= vp.vy && py < vp.vy + vp.H;
}
